import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import WarehouseFormDialog from './WarehouseFormDialog';
import type { WarehouseFormValues } from './WarehouseFormDialog';
import ZoneFormDialog from './ZoneFormDialog';
import type { ZoneFormValues } from './ZoneFormDialog';
import LocationFormDialog from './LocationFormDialog';
import type { LocationFormValues } from './LocationFormDialog';
import QrLabelPrintDialog from './QrLabelPrintDialog';
import type { QrLabelItem } from './QrLabelPrintDialog';
import type {
  BranchDto,
  CreateLocationRequest,
  LocationDto,
  UpdateLocationRequest,
  WarehouseDto,
  ZoneDto,
} from '@/types/organization';

export default function WarehouseMaster() {
  const { t } = useTranslation('warehouseMaster');
  const queryClient = useQueryClient();

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });

  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: async () => (await apiClient.get<BranchDto[]>('/branches')).data,
  });

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('');
  useEffect(() => {
    if (!selectedWarehouseId && warehouses.length > 0) setSelectedWarehouseId(warehouses[0].id);
  }, [warehouses, selectedWarehouseId]);

  const selectedWarehouse = warehouses.find((w) => w.id === selectedWarehouseId);
  const selectedBranch = selectedWarehouse ? branches.find((b) => b.id === selectedWarehouse.branchId) : undefined;
  const branchWarehouseCount = selectedWarehouse ? warehouses.filter((w) => w.branchId === selectedWarehouse.branchId).length : 0;

  const { data: zones = [] } = useQuery({
    queryKey: ['zones', selectedWarehouseId],
    queryFn: async () => (await apiClient.get<ZoneDto[]>('/zones', { params: { warehouseId: selectedWarehouseId } })).data,
    enabled: Boolean(selectedWarehouseId),
  });

  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  useEffect(() => setSelectedZoneId(null), [selectedWarehouseId]);

  const { data: locations = [], isLoading: locationsLoading } = useQuery({
    queryKey: ['locations', selectedWarehouseId, selectedZoneId],
    queryFn: async () =>
      (
        await apiClient.get<LocationDto[]>('/locations', {
          params: { warehouseId: selectedWarehouseId, zoneId: selectedZoneId ?? undefined },
        })
      ).data,
    enabled: Boolean(selectedWarehouseId),
  });

  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<LocationDto | null>(null);
  const [confirmDeleteLocation, setConfirmDeleteLocation] = useState<LocationDto | null>(null);
  const [qrPrintItems, setQrPrintItems] = useState<QrLabelItem[] | null>(null);

  const saveWarehouseMutation = useMutation({
    mutationFn: (values: WarehouseFormValues) => apiClient.post('/warehouses', values),
    onSuccess: async (response) => {
      await queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      setSelectedWarehouseId(response.data.id);
      setWarehouseDialogOpen(false);
    },
  });

  const saveZoneMutation = useMutation({
    mutationFn: (values: ZoneFormValues) => apiClient.post('/zones', { ...values, warehouseId: selectedWarehouseId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zones', selectedWarehouseId] });
      setZoneDialogOpen(false);
    },
  });

  const saveLocationMutation = useMutation({
    mutationFn: (values: LocationFormValues) => {
      const zoneId = values.zoneId || null;
      if (editingLocation) {
        const payload: UpdateLocationRequest = { ...values, zoneId, status: editingLocation.status };
        return apiClient.put(`/locations/${editingLocation.id}`, payload);
      }
      const payload: CreateLocationRequest = { ...values, zoneId, warehouseId: selectedWarehouseId };
      return apiClient.post('/locations', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations', selectedWarehouseId, selectedZoneId] });
      queryClient.invalidateQueries({ queryKey: ['zones', selectedWarehouseId] });
      setLocationDialogOpen(false);
      setEditingLocation(null);
    },
  });

  const deleteLocationMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/locations/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations', selectedWarehouseId, selectedZoneId] });
      setConfirmDeleteLocation(null);
    },
  });

  const columns: DataTableColumn<LocationDto>[] = [
    { field: 'code', headerName: t('table.code') },
    { field: 'locationType', headerName: t('table.locationType'), filterable: true },
    { field: 'storageType', headerName: t('table.storageType'), filterable: true },
    { field: 'zoneName', headerName: t('table.zone'), render: (r) => r.zoneName ?? '—', filterable: true },
    {
      field: 'capacity',
      headerName: t('table.capacity'),
      render: (r) =>
        r.maxVolume ? (
          <Box sx={{ minWidth: 120 }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (r.currentVolume / r.maxVolume) * 100)}
              sx={{ mb: 0.5 }}
            />
            <Typography variant="caption" color="text.secondary">
              {r.currentVolume} / {r.maxVolume} m³
            </Typography>
          </Box>
        ) : (
          '—'
        ),
    },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
          <IconButton
            size="small"
            title={t('qrPrint.printLocationTooltip')}
            onClick={() =>
              setQrPrintItems([
                {
                  code: r.code,
                  title: r.code,
                  subtitle: [r.locationType, r.zoneName, selectedWarehouse?.name].filter(Boolean).join(' · '),
                },
              ])
            }
          >
            <QrCode2Icon fontSize="small" />
          </IconButton>
          <IfPermitted permission={PERMISSIONS.WAREHOUSE_MANAGE}>
            <Button
              size="small"
              onClick={() => {
                setEditingLocation(r);
                setLocationDialogOpen(true);
              }}
            >
              Edit
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDeleteLocation(r)}>
              Delete
            </Button>
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.WAREHOUSE_MANAGE}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setWarehouseDialogOpen(true)}>
              {t('addWarehouse')}
            </Button>
          </IfPermitted>
        }
      />

      {warehouses.length === 0 ? (
        <Alert severity="info">{t('noWarehouses')}</Alert>
      ) : (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label={t('selectWarehouse')}
              value={selectedWarehouseId}
              onChange={(e) => setSelectedWarehouseId(e.target.value)}
              sx={{ minWidth: 260 }}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name} ({w.code})
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              <Chip
                label={t('allZones')}
                color={selectedZoneId === null ? 'primary' : 'default'}
                onClick={() => setSelectedZoneId(null)}
              />
              {zones.map((z) => (
                <Stack key={z.id} direction="row" alignItems="center" spacing={0.25}>
                  <Chip
                    label={`${z.name} (${z.locationCount})`}
                    color={selectedZoneId === z.id ? 'primary' : 'default'}
                    onClick={() => setSelectedZoneId(z.id)}
                  />
                  <IconButton
                    size="small"
                    title={t('qrPrint.printZoneTooltip')}
                    onClick={() =>
                      setQrPrintItems([
                        { code: z.code, title: z.name, subtitle: selectedWarehouse ? `${selectedWarehouse.name} · Zone` : 'Zone' },
                      ])
                    }
                  >
                    <QrCode2Icon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <IfPermitted permission={PERMISSIONS.WAREHOUSE_MANAGE}>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setZoneDialogOpen(true)}>
                  {t('addZone')}
                </Button>
              </IfPermitted>
            </Stack>
          </Stack>

          {selectedBranch && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                {t('branchLabel', { branch: selectedBranch.name })}
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                color={selectedBranch.maxWarehouses !== null && branchWarehouseCount >= selectedBranch.maxWarehouses ? 'warning' : 'default'}
                label={
                  selectedBranch.maxWarehouses !== null
                    ? t('warehouseLimitLabel', { count: branchWarehouseCount, limit: selectedBranch.maxWarehouses })
                    : t('warehouseLimitUnlimitedLabel', { count: branchWarehouseCount })
                }
              />
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1.5 }}>
            <IfPermitted permission={PERMISSIONS.WAREHOUSE_MANAGE}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingLocation(null);
                  setLocationDialogOpen(true);
                }}
              >
                {t('addLocation')}
              </Button>
            </IfPermitted>
          </Stack>

          <DataTable columns={columns} rows={locations} loading={locationsLoading} searchable getRowId={(r) => r.id} exportFileName="locations" />
        </>
      )}

      <WarehouseFormDialog
        open={warehouseDialogOpen}
        warehouse={null}
        onClose={() => setWarehouseDialogOpen(false)}
        onSubmit={(values) => saveWarehouseMutation.mutate(values)}
        loading={saveWarehouseMutation.isPending}
      />

      <ZoneFormDialog
        open={zoneDialogOpen}
        onClose={() => setZoneDialogOpen(false)}
        onSubmit={(values) => saveZoneMutation.mutate(values)}
        loading={saveZoneMutation.isPending}
      />

      <LocationFormDialog
        open={locationDialogOpen}
        location={editingLocation}
        zones={zones}
        onClose={() => {
          setLocationDialogOpen(false);
          setEditingLocation(null);
        }}
        onSubmit={(values) => saveLocationMutation.mutate(values)}
        loading={saveLocationMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDeleteLocation)}
        message={t('deleteLocation.message')}
        loading={deleteLocationMutation.isPending}
        onConfirm={() => confirmDeleteLocation && deleteLocationMutation.mutate(confirmDeleteLocation.id)}
        onClose={() => setConfirmDeleteLocation(null)}
      />

      <QrLabelPrintDialog open={Boolean(qrPrintItems)} items={qrPrintItems ?? []} onClose={() => setQrPrintItems(null)} />
    </>
  );
}
